import { useState } from "react";
import { IonButton, useIonRouter } from "@ionic/react";
import { useAllDatesTimes } from "components/AllDatesTimes";
import { EditRequestModal } from "components/EditRequest/EditRequestModal";
import { RequestModel, RequestStatus } from "model";
import { Link } from "react-router-dom";
import { createRequestASubPath } from "routes/routeData";
import { Alert, DataItemView, useFeatureFlags } from "swing-components";

import { useCancelRequest } from "~components/CancelRequest";
import { useRequestDetailsAlert } from "~components/RequestInfoCard/hooks";
import { translate, useURLSearchParams } from "~utils";

type RequestInfoCardProps = {
  canModify: boolean;
  request: RequestModel;
};

export function RequestInfoCard(props: RequestInfoCardProps) {
  const { canModify, request } = props;
  const { flags } = useFeatureFlags();

  /***** State *****/
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { alertTitle, alertMessage, alertColor, alertDisclaimer } = useRequestDetailsAlert();
  const { openCancelRequestModal } = useCancelRequest(request);

  const isLMRTrimEnabled = Boolean(flags?.lmrTrimEnabled);
  /***** Handlers *****/
  function handleCancel() {
    openCancelRequestModal();
  }

  function handleEditOpen() {
    setIsEditModalOpen(true);
  }

  function handleEditClose() {
    setIsEditModalOpen(false);
  }

  const hasAlerts = alertTitle || alertMessage;

  /***** Render *****/
  return (
    <>
      {/* Alerts */}
      {hasAlerts && (
        <div css={{ marginBottom: 16 }}>
          <Alert color={alertColor || "success"} title={alertTitle} showIcon>
            <div css={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
              {alertMessage && <EditRequestAlertContent alertMessage={alertMessage} />}
              {alertDisclaimer && <p css={{ marginTop: 0 }}>{alertDisclaimer}</p>}
            </div>
          </Alert>
        </div>
      )}
      {/* When an UNFILLED MD/LT request start date has been auto trimmed within the last 24 hours */}
      {request.status === RequestStatus.Submitted && request.recentlyTrimmed && (
        <div css={{ marginBottom: 16 }}>
          <Alert
            color="info"
            showIcon
            title={translate("REQUEST_ALERT_START_DATE_AUTO_ADJUSTED_TITLE")}
          >
            <p css={{ margin: 0, fontSize: 14, lineHeight: "24px" }}>
              {/*
               ** Trimmed LTRs include an extra event type, which we use to
               ** differentiate MD vs LT requests and show appropriate copy.
               */}
              {translate("REQUEST_ALERT_START_DATE_AUTO_ADJUSTED")}
            </p>
          </Alert>
        </div>
      )}
      {/* Only show the pending LT request alert if the color is not a warning */}
      {/* This is to prevent duplicate alerts from being shown */}
      {request.isLTRequestPending() && alertColor !== "warning" && (
        <div css={{ marginBottom: 16 }}>
          <Alert color="warning" title={translate("LABEL_PENDING_SWING_REVIEW_TITLE")} showIcon>
            {translate("LABEL_PENDING_SWING_REVIEW_MESSAGE")}
          </Alert>
        </div>
      )}
      {isLMRTrimEnabled && request?.subArrivalTime && (
        <Alert color="disclaimer" css={{ marginBottom: 16 }}>
          <span>{translate("LABEL_SUB_ARRIVAL")}&nbsp;</span>
          <span css={{ fontWeight: "bold" }}>{request.subArrivalTime?.toFormat("h:mm a")}</span>
        </Alert>
      )}

      <ButtonGroup
        id={request.id}
        onEdit={handleEditOpen}
        onCancel={handleCancel}
        isActiveRequest={canModify && !request.isCanceled() && !request.isPast()}
      />
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          paddingBottom: 16,
        }}
      >
        <DateDetail request={request} />
        <SchoolDetail request={request} />
      </div>
      {isEditModalOpen && <EditRequestModal request={request} onClose={handleEditClose} />}
    </>
  );
}

/***** Helper Components *****/
type ButtonGroupProps = {
  id?: string;
  isActiveRequest?: boolean;
  onCancel: () => void;
  onEdit: () => void;
};

function ButtonGroup(props: ButtonGroupProps) {
  const { id, isActiveRequest, onCancel, onEdit } = props;
  // We only want to show the Duplicate button in past requests
  if (!isActiveRequest) {
    return (
      <div css={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <DuplicateButton id={id} />
      </div>
    );
  }

  return (
    <div css={{ display: "flex", gap: 16, marginBottom: 16 }}>
      <IonButton id={`open-edit-modal-${id}`} className="w-full" onClick={onEdit}>
        {translate("LABEL_EDIT")}
      </IonButton>
      <DuplicateButton id={id} />
      <IonButton fill="outline" className="w-full" onClick={onCancel}>
        {translate("LABEL_CANCEL")}
      </IonButton>
    </div>
  );
}

type DuplicateButtonProps = Pick<ButtonGroupProps, "id">;

function DuplicateButton(props: DuplicateButtonProps) {
  const { id } = props;
  const router = useIonRouter();
  const searchParams = useURLSearchParams();
  searchParams.set("clonedFrom", id || "");

  return (
    <IonButton
      fill="outline"
      className="w-full"
      onClick={() => router.push(`${createRequestASubPath()}?${searchParams.toString()}`)}
    >
      {translate("REQUEST_CARD_LABEL_DUPLICATE")}
    </IonButton>
  );
}

type BaseRequestDetailProps = { request: RequestModel };

function DateDetail(props: BaseRequestDetailProps) {
  const { request } = props;
  const { flags } = useFeatureFlags();

  const clientDate = request.clientDate;
  const clientTime = request.clientTime;
  const isMultiDayRequest = request.isMultiDayRequest();

  const latestAcceptableStart = request.latestAcceptableStart;
  const requestStart = request.startTime;

  const showLatestStartDisclaimer =
    Boolean(flags?.lmrTrimEnabled) &&
    latestAcceptableStart &&
    latestAcceptableStart != requestStart;

  const { openAllDatesTimesModal } = useAllDatesTimes(request);

  return (
    <DataItemView
      stackLayout
      icon={isMultiDayRequest ? "Calendar Overlap" : "Calendar Today"}
      label={<h3 css={{ margin: 0 }}>{clientDate}</h3>}
    >
      {!isMultiDayRequest && (
        <>
          <p css={{ margin: 0 }}>{clientTime}</p>
          {showLatestStartDisclaimer && (
            <div css={{ margin: "8px 0" }}>
              <span css={{ fontWeight: "bold" }}>{translate("LABEL_REQ_STAYS_OPEN")}</span>
              <br />
              <span>{translate("LABEL_LATEST_ARRIVAL")}</span>{" "}
              <span css={{ fontWeight: "bold" }}>{latestAcceptableStart.toFormat("h:mm a")}</span>
            </div>
          )}
        </>
      )}
      {isMultiDayRequest && (
        <a
          href="/"
          className="block swing-link"
          onClick={(e) => {
            e.preventDefault();
            openAllDatesTimesModal();
          }}
        >
          <p css={{ margin: 0 }}>{translate("MULTI_DAY_ALL_DATES_TIMES_LABEL")}</p>
        </a>
      )}
    </DataItemView>
  );
}

function SchoolDetail(props: BaseRequestDetailProps) {
  const { request } = props;
  const school = request.school;

  return (
    <DataItemView
      stackLayout
      icon="School"
      label={
        <>
          <Link to={`/schools/${school.id}`} css={{ textDecoration: "none" }}>
            {school.name}
          </Link>
          <p css={{ margin: 0, fontWeight: 400 }}>
            <>
              {school.streetAddress && school.streetAddress}
              <br />
              {school.city && (
                <>
                  {school.city}, {school.state} {school.zip}
                </>
              )}
            </>
          </p>
        </>
      }
    />
  );
}

type EditRequestAlertContentProps = {
  alertMessage: string | string[];
};

function EditRequestAlertContent(props: EditRequestAlertContentProps) {
  const { alertMessage } = props;

  if (!Array.isArray(alertMessage)) {
    return alertMessage;
  }

  if (alertMessage.length === 0) {
    return null;
  }

  return (
    <div css={{ display: "flex", flexDirection: "column", margin: 0, marginBottom: 16 }}>
      <ul css={{ paddingLeft: 0, margin: 0 }}>
        {alertMessage.map((message) => {
          const transformedMessage = transformAlertMessage(message);
          if (!transformedMessage) return null;

          return <li key={message}>{transformedMessage}</li>;
        })}
      </ul>
    </div>
  );
}

function transformAlertMessage(message: string) {
  switch (message) {
    case "school":
      return translate("SCHOOL_LABEL");
    case "intervals":
    case "startDate":
    case "endDate":
      return translate("LABEL_DATES_TIMES");
    case "teacher":
      return translate("LABEL_TEACHER");
    case "grade":
      return translate("LABEL_GRADE");
    case "subject":
      return translate("LABEL_SUBJECT");
    case "contactPhone":
      return translate("LABEL_CONTACT_NUMBER");
    case "internalCode":
      return translate("LABEL_INTERNAL_CODE");
    case "isCredentialRequired":
      return translate("LABEL_CRED_PERMIT");
    case "isLessonPlanProvided":
      return translate("LABEL_LESSON_PLAN");
    case "budgetId":
      return translate("LABEL_BUDGET");
    case "position":
      return translate("LABEL_POSITION");
    case "memo":
      return translate("NOTES_TO_SUB_LABEL");
    default:
      return "";
  }
}
